import { useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtBR, fmtMoney } from '../lib/format';
import { buildCategoriaOptions } from '../lib/ui';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';
import type { ExtratoLancamento, PlanoConta, ExtratoSugestao, ExtratoCandidato } from '../types';

const GRID = '78px minmax(200px,1.9fr) 118px 120px 118px 150px';
// Débitos "COB" até este valor são tarifas bancárias. Créditos "COB" são receitas (qualquer valor).
const TARIFA_MAX = 100;

// Só a parte YYYY-MM-DD (o backend do extrato devolve a data como timestamp ISO).
function isoDate(d: string): string {
  const m = String(d).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : String(d).slice(0, 10);
}

type CobKind = 'tarifa' | 'receita' | null;
// Regra: histórico contém "COB". Débito pequeno → tarifa (despesa). Crédito → receita.
function cobKind(e: ExtratoLancamento): CobKind {
  if (!/\bCOB\b/i.test(e.descricao || '')) return null;
  if (e.tipo === 'C') return 'receita';
  if (e.tipo === 'D' && Math.abs(e.valor) <= TARIFA_MAX) return 'tarifa';
  return null;
}

function acharCatTarifa(items: PlanoConta[]): number | null {
  const cat = items.find((p) => p.nivel === 3 && p.ativo && /tarifa|banc[áa]ri/i.test(p.nome));
  return cat?.id ?? null;
}

export function Extrato(_props: PageProps) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const xlsxRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [tipo, setTipo] = useState<'todos' | 'D' | 'C'>('todos');
  const [conc, setConc] = useState<'todos' | 'sim' | 'nao'>('todos');
  const [vincular, setVincular] = useState<ExtratoLancamento | null>(null);
  const [lancar, setLancar] = useState<ExtratoLancamento | null>(null);

  const list = useFetch(() => api.listExtrato({ limit: 2000 }), []);
  const plano = useFetch(() => api.listPlano(), []);
  // Sugestões calculadas no backend (valor ±2% + data ±5 dias) sobre TODAS as contas
  const sug = useFetch(() => api.extratoSugestoes(), []);
  const catTarifa = plano.data ? acharCatTarifa(plano.data.items) : null;
  const catOptions = plano.data ? buildCategoriaOptions(plano.data.items) : [];

  const sugMap = useMemo(() => {
    const m = new Map<number, ExtratoSugestao>();
    for (const s of sug.data?.items ?? []) m.set(Number(s.extrato_id), s);
    return m;
  }, [sug.data]);

  const rows = useMemo(() => {
    let r = list.data?.items ?? [];
    if (de) r = r.filter((e) => isoDate(e.data) >= de);
    if (ate) r = r.filter((e) => isoDate(e.data) <= ate);
    if (tipo !== 'todos') r = r.filter((e) => e.tipo === tipo);
    if (conc !== 'todos') r = r.filter((e) => (conc === 'sim' ? e.conciliado : !e.conciliado));
    return r;
  }, [list.data, de, ate, tipo, conc]);

  // Pendentes classificáveis (tarifa depende da categoria existir; receita não)
  const classificaveis = useMemo(
    () => rows.filter((e) => { const k = cobKind(e); return !e.conciliado && (k === 'receita' || (k === 'tarifa' && catTarifa)); }),
    [rows, catTarifa],
  );

  const importar = (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    api.importExtrato(file, 'CEF')
      .then((r) => { toast(`Importado: ${r.inserted} novos, ${r.skipped} já existentes.`); recarregar(); })
      .catch(() => toast('Falha ao importar extrato.', 'err'))
      .finally(() => setBusy(false));
  };

  // XLSX da Caixa com nome + CPF/CNPJ do favorecido: preenche a identidade dos
  // lançamentos já importados por OFX (não insere linhas novas).
  const enriquecer = (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    api.enriquecerExtrato(file)
      .then((r) => {
        const resto = r.sem_correspondencia_total ? ` ${r.sem_correspondencia_total} sem lançamento correspondente.` : '';
        toast(`Identidade: ${r.atualizadas} lançamento(s) atualizados (${r.casadas}/${r.com_identidade} casados).${resto}`);
        recarregar();
      })
      .catch(() => toast('Falha ao enriquecer extrato (esperado XLSX do extrato Caixa).', 'err'))
      .finally(() => setBusy(false));
  };

  // Cria o lançamento (despesa=tarifa ou receita) e concilia a linha do extrato.
  const classificar = async (e: ExtratoLancamento): Promise<boolean> => {
    const k = cobKind(e);
    if (!k) return false;
    if (k === 'tarifa' && !catTarifa) { toast('Crie a categoria "Tarifas bancárias" no Plano de Contas primeiro.', 'err'); return false; }
    const r = await api.criarConta({
      tipo: k === 'tarifa' ? 'despesa' : 'receita',
      fornecedor: k === 'tarifa' ? 'Tarifa bancária' : 'Recebimento (COB)',
      descricao: e.descricao,
      valor: Math.abs(e.valor),
      vencimento: isoDate(e.data),
      plano_conta_id: k === 'tarifa' ? catTarifa : undefined,
      forma_pagamento: 'outro',
    });
    await api.vincularExtrato(e.id, r.ids[0]);
    return true;
  };

  const lancarUm = async (e: ExtratoLancamento) => {
    try {
      if (await classificar(e)) { toast(cobKind(e) === 'receita' ? 'Receita lançada e conciliada.' : 'Tarifa lançada e conciliada.'); recarregar(); }
    } catch (err) {
      const body = (err as { body?: { error?: string; detail?: string } }).body;
      toast(`Falha ao lançar${body?.error ? ` (${body.error})` : ''}.`, 'err');
    }
  };

  const lancarTodos = async () => {
    if (classificaveis.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const e of classificaveis) { try { if (await classificar(e)) ok++; } catch { /* segue */ } }
    toast(`${ok} lançamento(s) classificado(s) e conciliado(s).`);
    recarregar();
    setBusy(false);
  };

  const recarregar = () => { list.refetch(); sug.refetch(); };
  const limpar = de || ate || tipo !== 'todos' || conc !== 'todos';

  return (
    <div className="page mid">
      <div className="page-head">
        <h1>Extrato / Conciliação</h1>
        {classificaveis.length > 0 && (
          <button className="btn ghost green" onClick={lancarTodos} disabled={busy}>
            <Icon name="check" size={14} />Classificar {classificaveis.length} item{classificaveis.length > 1 ? 's' : ''}
          </button>
        )}
        <button className="btn ghost" onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? 'Processando…' : 'Importar OFX'}</button>
        <input ref={fileRef} type="file" hidden accept=".ofx,.csv" onChange={(e) => importar(e.target.files?.[0])} />
        <button className="btn ghost" title="XLSX do extrato Caixa com nome e CPF/CNPJ do favorecido" onClick={() => xlsxRef.current?.click()} disabled={busy}>Enriquecer XLSX</button>
        <input ref={xlsxRef} type="file" hidden accept=".xlsx" onChange={(e) => { enriquecer(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {/* filtros */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', marginBottom: 14 }}>
        <span className="k-label" style={{ fontSize: 11 }}>Período</span>
        <span className="text-sm muted">de</span>
        <input type="date" className="input sm mono" style={{ width: 'auto' }} value={de} onChange={(e) => setDe(e.target.value)} />
        <span className="text-sm muted">até</span>
        <input type="date" className="input sm mono" style={{ width: 'auto' }} value={ate} onChange={(e) => setAte(e.target.value)} />
        <select className="select sm" style={{ width: 'auto' }} value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="todos">Débitos e créditos</option>
          <option value="D">Só débitos</option>
          <option value="C">Só créditos</option>
        </select>
        <select className="select sm" style={{ width: 'auto' }} value={conc} onChange={(e) => setConc(e.target.value as typeof conc)}>
          <option value="todos">Conciliação: todos</option>
          <option value="sim">Só conciliados</option>
          <option value="nao">Só pendentes</option>
        </select>
        {limpar && <span onClick={() => { setDe(''); setAte(''); setTipo('todos'); setConc('todos'); }} style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', cursor: 'pointer' }}>✕ limpar</span>}
        <span className="text-sm muted" style={{ marginLeft: 'auto' }}>{rows.length} lançamento{rows.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="tbl tbl-scroll">
        <div style={{ minWidth: 860 }}>
          <div className="tbl-head" style={{ gridTemplateColumns: GRID }}>
            <span>Data</span><span>Histórico</span><span className="cell-r">Valor</span><span className="cell-r">Saldo</span><span>Conciliação</span><span></span>
          </div>
          {/* só colapsa para "Carregando…" no load inicial; no refetch pós-conciliar
              mantém as linhas para não encolher a página e perder a posição do scroll */}
          {list.loading && !list.data ? (
            <div className="loading">Carregando…</div>
          ) : rows.length === 0 ? (
            <div className="loading">Nenhum lançamento neste filtro. Importe um extrato OFX/CSV.</div>
          ) : (
            rows.map((e) => (
              <Row key={e.id} e={e} kind={cobKind(e)} sugestao={sugMap.get(Number(e.id)) ?? null}
                onLancar={() => lancarUm(e)} onVincular={() => setVincular(e)} onLancarConta={() => setLancar(e)} />
            ))
          )}
        </div>
      </div>

      {vincular && (
        <VincularModal
          e={vincular}
          preselect={sugMap.get(Number(vincular.id))?.conta_id ?? vincular.conta_a_pagar_id ?? null}
          onClose={() => setVincular(null)}
          onDone={() => { setVincular(null); recarregar(); }}
        />
      )}

      {lancar && (
        <LancarContaModal
          e={lancar}
          catOptions={catOptions}
          onClose={() => setLancar(null)}
          onDone={() => { setLancar(null); recarregar(); }}
        />
      )}
    </div>
  );
}

function Row({ e, kind, sugestao, onLancar, onVincular, onLancarConta }:
  { e: ExtratoLancamento; kind: CobKind; sugestao: ExtratoSugestao | null; onLancar: () => void; onVincular: () => void; onLancarConta: () => void }) {
  const debito = e.tipo === 'D';
  const sub = [e.fornecedor, e.contraparte, e.documento && `doc ${e.documento}`].filter(Boolean).join(' · ');
  return (
    <div className="tbl-row" style={{ gridTemplateColumns: GRID }}>
      <span className="mono text-sm muted">{fmtBR(e.data)}</span>
      <div className="cell-2">
        <div className="l1 row" style={{ gap: 6 }}>
          <span className="ellipsis">{e.descricao || '—'}</span>
          {kind === 'tarifa' && <span className="chip-sq info" style={{ fontSize: 10, padding: '1px 6px' }}>Tarifa</span>}
          {kind === 'receita' && <span className="chip-sq" style={{ fontSize: 10, padding: '1px 6px', background: 'var(--green-bg)', color: 'var(--green)' }}>Receita</span>}
        </div>
        {sub && <div className="l2">{sub}</div>}
        {!e.conciliado && sugestao && (
          <div className="l2" style={{ color: 'var(--green)', fontWeight: 600 }}>
            ↔ sugestão: {sugestao.fornecedor}{sugestao.status === 'pago' ? ' (pago)' : ''} · {sugestao.dias === 0 ? 'mesma data' : `${sugestao.dias}d`}{sugestao.cnpj_bate ? ' · CNPJ bate' : ''}
          </div>
        )}
      </div>
      <span className="mono cell-r" style={{ color: debito ? 'var(--danger)' : 'var(--green)', fontSize: 12.5 }}>
        {debito ? '−' : '+'}{fmtMoney(Math.abs(e.valor))}
      </span>
      <span className="mono cell-r text-sm" style={{ color: 'var(--text-2)' }}>{fmtMoney(e.saldo)}</span>
      <span>
        {e.conciliado ? (
          <span className="row" style={{ gap: 5, color: 'var(--green)', fontSize: 12, fontWeight: 600 }}><Icon name="check" size={14} />conciliado</span>
        ) : (
          <span className="row" style={{ gap: 5, color: 'var(--amber)', fontSize: 12, fontWeight: 600 }}><Icon name="alert" size={13} />pendente</span>
        )}
      </span>
      <span className="cell-r" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        {e.conciliado ? (
          <button className="btn ghost xs" onClick={onVincular}>Rever</button>
        ) : kind ? (
          <button className="btn ghost green xs" onClick={onLancar}>Lançar {kind === 'receita' ? 'receita' : 'tarifa'}</button>
        ) : (
          <>
            {sugestao
              ? <button className="btn ghost green xs" onClick={onVincular}>Conciliar</button>
              : <button className="btn ghost xs" onClick={onVincular}>Vincular</button>}
            {debito && <button className="btn ghost xs" title="Criar conta a pagar já quitada a partir deste débito" onClick={onLancarConta}>Lançar conta</button>}
          </>
        )}
      </span>
    </div>
  );
}

// Item exibível no modal (candidato do backend ou resultado de busca)
interface Opcao {
  id: number; fornecedor: string; descricao: string | null;
  valor_liquido: number | null; valor: number | null;
  status: string; vencimento: string | null; dias?: number; match: boolean;
  cnpj_bate?: boolean;
}

function VincularModal({ e, preselect, onClose, onDone }:
  { e: ExtratoLancamento; preselect: number | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [sel, setSel] = useState<number | null>(preselect);
  const [busca, setBusca] = useState('');
  const [busy, setBusy] = useState(false);

  const cand = useFetch(() => api.extratoCandidatos(e.id), [e.id]);
  const busGrep = busca.trim();
  const search = useFetch(
    () => (busGrep.length >= 2 ? api.listContas({ search: busGrep, status: 'todos', limit: 40 }) : Promise.resolve({ items: [], total: 0, count: 0 })),
    [busGrep],
  );

  const opcoes: Opcao[] = useMemo(() => {
    if (busGrep.length >= 2) {
      return (search.data?.items ?? []).map((c) => ({
        id: Number(c.id), fornecedor: c.fornecedor, descricao: c.descricao,
        valor_liquido: c.valor_liquido, valor: c.valor, status: c.status,
        vencimento: c.vencimento,
        match: Math.abs((c.valor_liquido ?? c.valor ?? 0) - Math.abs(e.valor)) <= Math.max(0.02 * Math.abs(e.valor), 0.05),
      }));
    }
    return (cand.data?.items ?? []).map((c: ExtratoCandidato) => ({
      id: c.id, fornecedor: c.fornecedor, descricao: c.descricao,
      valor_liquido: c.valor_liquido, valor: c.valor, status: c.status,
      vencimento: c.vencimento, dias: c.dias, match: true, cnpj_bate: c.cnpj_bate,
    }));
  }, [busGrep, search.data, cand.data, e.valor]);

  const carregando = busGrep.length >= 2 ? search.loading : cand.loading;

  const salvar = async (contaId: number | null) => {
    setBusy(true);
    try {
      await api.vincularExtrato(e.id, contaId);
      toast(contaId ? 'Débito conciliado com a conta.' : 'Vínculo removido.');
      onDone();
    } catch (err) {
      const body = (err as { body?: { error?: string } }).body;
      toast(`Falha ao vincular${body?.error ? ` (${body.error})` : ''}.`, 'err');
      setBusy(false);
    }
  };

  return (
    <Modal title="Conciliar débito com conta a pagar" width={640} onClose={onClose}
      footer={<>
        {e.conta_a_pagar_id && <button className="btn ghost" style={{ color: 'var(--danger)' }} onClick={() => salvar(null)} disabled={busy}>Desvincular</button>}
        <button className="btn ghost" onClick={onClose} style={{ marginLeft: 'auto' }}>Cancelar</button>
        <button className="btn primary" onClick={() => salvar(sel)} disabled={busy || !sel}>Vincular</button>
      </>}>
      <div className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '8px 12px', marginBottom: 12 }}>
        <div><div className="k-label" style={{ fontSize: 10 }}>Débito</div><div className="mono">{fmtBR(e.data)}</div></div>
        <div><div className="k-label" style={{ fontSize: 10 }}>Valor</div><div className="mono cell-strong" style={{ color: 'var(--danger)' }}>−{fmtMoney(Math.abs(e.valor))}</div></div>
        <div className="ellipsis"><div className="k-label" style={{ fontSize: 10 }}>Histórico</div><div className="text-sm ellipsis">{e.descricao || '—'}</div></div>
      </div>

      <div className="searchbox" style={{ marginBottom: 10 }}>
        <Icon name="search" size={14} className="muted" />
        <input placeholder="Buscar por fornecedor ou descrição…" value={busca} onChange={(ev) => setBusca(ev.target.value)} />
      </div>

      <div className="k-label" style={{ fontSize: 11, marginBottom: 6 }}>
        {busGrep.length >= 2
          ? `Resultados da busca "${busGrep}"`
          : opcoes.length ? `${opcoes.length} sugestão(ões) — valor compatível e data a ±5 dias` : 'Nenhuma sugestão automática — use a busca acima'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
        {carregando ? (
          <div className="loading">Carregando…</div>
        ) : opcoes.length === 0 ? (
          <div className="loading">{busGrep.length >= 2 ? 'Nenhuma conta encontrada.' : 'Sem candidatos — busque manualmente.'}</div>
        ) : (
          opcoes.map((c) => {
            const on = sel === c.id;
            const pago = c.status === 'pago';
            return (
              <div key={c.id} onClick={() => setSel(c.id)}
                className="card" style={{
                  display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center',
                  padding: '8px 12px', cursor: 'pointer',
                  border: on ? '2px solid var(--primary)' : '1px solid var(--border)',
                }}>
                <div className="ellipsis">
                  <div className="l1 row" style={{ gap: 6 }}>
                    <span className="ellipsis">{c.fornecedor}</span>
                    {c.cnpj_bate && <span className="chip-sq" style={{ fontSize: 10, padding: '1px 6px', background: 'var(--green-bg)', color: 'var(--green)', fontWeight: 700 }}>CNPJ bate</span>}
                    {c.match && <span className="chip-sq" style={{ fontSize: 10, padding: '1px 6px', background: 'var(--green-bg)', color: 'var(--green)' }}>valor bate</span>}
                    {typeof c.dias === 'number' && <span className="chip-sq info" style={{ fontSize: 10, padding: '1px 6px' }}>{c.dias === 0 ? 'mesma data' : `${c.dias}d`}</span>}
                  </div>
                  <div className="l2 ellipsis">{c.descricao || '—'} · venc {fmtBR(c.vencimento)}</div>
                </div>
                <span className={`badge ${pago ? 'green' : 'gray'}`}>{pago ? 'Pago' : c.status}</span>
                <span className="mono cell-strong">{fmtMoney(c.valor_liquido ?? c.valor ?? 0)}</span>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}

// Cria uma conta a pagar JÁ QUITADA a partir de um débito do extrato (o extrato é o
// que aconteceu de verdade) e concilia o lançamento. NF fica pendente até chegar,
// salvo se o usuário marcar "não terá nota fiscal".
function LancarContaModal({ e, catOptions, onClose, onDone }:
  { e: ExtratoLancamento; catOptions: { id: number; label: string }[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [fornecedor, setFornecedor] = useState(e.contraparte ?? e.fornecedor ?? '');
  const [descricao, setDescricao] = useState(e.descricao ?? '');
  const [cat, setCat] = useState<number | ''>('');
  const [semNf, setSemNf] = useState(false);
  const [busy, setBusy] = useState(false);

  const salvar = async () => {
    if (!fornecedor.trim()) { toast('Informe o fornecedor.', 'err'); return; }
    setBusy(true);
    try {
      const r = await api.criarConta({
        tipo: 'despesa',
        fornecedor: fornecedor.trim(),
        descricao: descricao.trim() || null,
        valor: Math.abs(e.valor),
        vencimento: isoDate(e.data),
        cnpj_cpf: e.contraparte_doc || null,
        plano_conta_id: cat ? Number(cat) : undefined,
        nf_dispensada: semNf,
      });
      await api.vincularExtrato(e.id, r.ids[0]);
      toast('Conta lançada e conciliada com o débito.');
      onDone();
    } catch (err) {
      const body = (err as { body?: { error?: string } }).body;
      toast(`Falha ao lançar${body?.error ? ` (${body.error})` : ''}.`, 'err');
      setBusy(false);
    }
  };

  return (
    <Modal title="Lançar conta a partir do débito" width={560} onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy}>Lançar e conciliar</button>
      </>}>
      <div className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '8px 12px', marginBottom: 12 }}>
        <div><div className="k-label" style={{ fontSize: 10 }}>Data</div><div className="mono">{fmtBR(e.data)}</div></div>
        <div><div className="k-label" style={{ fontSize: 10 }}>Valor</div><div className="mono cell-strong" style={{ color: 'var(--danger)' }}>−{fmtMoney(Math.abs(e.valor))}</div></div>
        <div className="ellipsis"><div className="k-label" style={{ fontSize: 10 }}>Histórico</div><div className="text-sm ellipsis">{e.descricao || '—'}</div></div>
      </div>
      <div className="field"><label>Fornecedor</label><input className="input" value={fornecedor} onChange={(ev) => setFornecedor(ev.target.value)} placeholder="Nome do fornecedor" /></div>
      <div className="field"><label>Descrição</label><input className="input" value={descricao} onChange={(ev) => setDescricao(ev.target.value)} /></div>
      <label className="flabel">Categoria (opcional)</label>
      <select className="select" value={cat} onChange={(ev) => setCat(ev.target.value ? Number(ev.target.value) : '')}>
        <option value="">Classificar depois…</option>
        {catOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <label className="checkline" style={{ marginTop: 12 }}>
        <input type="checkbox" checked={semNf} onChange={(ev) => setSemNf(ev.target.checked)} />
        Esta despesa não terá nota fiscal (dispensar)
      </label>
      {!semNf && <div className="text-sm muted" style={{ marginTop: 6 }}>Ficará marcada como "paga sem NF" até a nota chegar.</div>}
    </Modal>
  );
}
